import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Grid, Typography, Button, Box, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Pagination, Tooltip, TextField, Popover
} from '@mui/material';
import {
  ArrowBack, ArrowLeft, ArrowRight, ZoomIn, ZoomOut, FileDownload, Folder, InsertDriveFile, DeleteOutline
} from '@mui/icons-material';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import ExtractedFields from './ExtractedFields';
import axiosInstance from './axiosInstance';
import { useAuthSession } from './AuthSessionContext';

const STRICT_DOC_TYPES = new Set(['invoice', 'estimate', 'credit_note']);
const STATEMENT_DOC_TYPE = 'statement';
const DATE_FIELDS = ['invoice_date', 'posting_date'];

const normalizeId = (value) => (typeof value === 'string' ? value.toLowerCase() : value);
const isEmptyValue = (value) => (
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '')
);

const isValidDate = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return false;

  let value = rawValue.trim();
  if (!value) return false;
  if (value.includes('T')) {
    value = value.split('T')[0];
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(`${year}-${month}-${day}`);
    return (
      date.getFullYear() === Number(year) &&
      date.getMonth() + 1 === Number(month) &&
      date.getDate() === Number(day)
    );
  }

  const ukMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ukMatch) {
    const [, day, month, year] = ukMatch;
    const date = new Date(`${year}-${month}-${day}`);
    return (
      date.getFullYear() === Number(year) &&
      date.getMonth() + 1 === Number(month) &&
      date.getDate() === Number(day)
    );
  }

  return false;
};

const STRICT_REQUIRED_FIELDS = [
  'location_id',
  'supplier_id',
  'nominal_code_id',
  'doc_status_id',
  'invoice_date',
  'invoice_number',
  'order_number',
  'net',
  'vat',
  'total',
];

const ReviewPage = ({
  detailedData, editableData, tempEditableData, sites, suppliers, nominalCodes, taxCodes, docTypes, docStatuses, docRejectReasons = [], 
  currentImageIndex, isEdited, transformWrapperRef, handleBackClick, handleFieldChange, 
  handleFieldBlur, handlePreviousImage: handlePreviousImageProp, handleNextImage: handleNextImageProp, handleSaveChanges, 
  handleApproveForImport, handleRejectDocument, otherDocuments, handleCardClick, getLocationOptionLabel,
  getSupplierOptionLabel, getNominalCodeOptionLabel, getDocTypeOptionLabel, getDocStatusOptionLabel, refetchData, imageName,
  hasPreviousDocument = false, hasNextDocument = false, onPreviousDocument = () => {}, onNextDocument = () => {},
  documentPosition = 0, documentCount = 0, documentLoading = false, isReadOnly = false,
  totalNetValue = 0,
  showOtherDocuments = true
}) => {
  const [polygons, setPolygons] = useState([]);
  const [polygonProperty, setPolygonProperty] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const suppressAutoFocusRef = useRef(false);
  const shouldAutoNavigateRef = useRef(false);
  const polygonPropertyRef = useRef(polygonProperty);
  const lineItems = Array.isArray(detailedData?.line_items) ? detailedData.line_items : [];
  const [lineItemsState, setLineItemsState] = useState(lineItems);
  const { roles: userRoles = [] } = useAuthSession();
  const isAdmin = userRoles.includes('admin');
  const [lineItemEdits, setLineItemEdits] = useState({});
  const [lineItemSaving, setLineItemSaving] = useState({});
  const [lineItemDeleting, setLineItemDeleting] = useState({});
  const [lineItemDocDiscount, setLineItemDocDiscount] = useState(null);
  const [lineItemStatus, setLineItemStatus] = useState({});
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addingLineItem, setAddingLineItem] = useState(false);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;
    return numericValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  };

  const formatQuantity = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;
    return numericValue % 1 === 0 ? numericValue.toString() : numericValue.toFixed(2);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') return '';
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return '';
    return `${numericValue.toFixed(2)}%`;
  };

  const formatPercentOrDash = (value) => {
    const formatted = formatPercent(value);
    return formatted ? formatted : '-';
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    if (value === '') return null;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  };

  const calculateLineDiscount = (quantity, retailPrice, actualPrice) => {
    if (quantity === null || retailPrice === null || actualPrice === null) return null;
    const qty = Math.abs(quantity);
    const retail = Math.abs(retailPrice);
    const actual = Math.abs(actualPrice);
    if (!qty || !retail || !actual) return null;
    const actualUnitPrice = actual / qty;
    if (retail < actualUnitPrice) return null;
    return Number(((1 - actualUnitPrice / retail) * 100).toFixed(2));
  };

  const getLineItemKey = (item, index) => (item?.id ? String(item.id) : `index-${index}`);

  const getLineItemValue = (item, key, field) => {
    if (lineItemEdits[key] && field in lineItemEdits[key]) {
      return lineItemEdits[key][field];
    }
    const value = item?.[field];
    return value === null || value === undefined ? '' : value;
  };

  const hasLineItemChanges = (item, key) => {
    const edited = lineItemEdits[key];
    if (!edited) return false;
    return ['part_number', 'description', 'quantity', 'retail_price', 'actual_price'].some((field) => {
      if (!(field in edited)) return false;
      if (field === 'part_number' || field === 'description') {
        const currentValue = (edited[field] ?? '').toString().trim();
        const originalValue = (item?.[field] ?? '').toString().trim();
        return currentValue !== originalValue;
      }
      const currentValue = parseNumber(edited[field]);
      const originalValue = parseNumber(item?.[field]);
      return currentValue !== originalValue;
    });
  };

  const hasNewLineItemData = (item, key) => {
    const fields = ['part_number', 'description', 'quantity', 'retail_price', 'actual_price'];
    return fields.some((field) => {
      const value = getLineItemValue(item, key, field);
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      return true;
    });
  };

  const handleLineItemChange = (key, field, value) => {
    setLineItemEdits((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const handleLineItemSave = async (item, index) => {
    if (!detailedData?.id) return;
    const key = getLineItemKey(item, index);
    const quantityPreview = parseNumber(getLineItemValue(item, key, 'quantity'));
    const retailPreview = parseNumber(getLineItemValue(item, key, 'retail_price'));
    const actualPreview = parseNumber(getLineItemValue(item, key, 'actual_price'));
    const discountPreview = calculateLineDiscount(quantityPreview, retailPreview, actualPreview);
    const discountChanged = discountPreview !== null && Number(discountPreview) !== Number(item.discount);
    const isNewRow = !!item.__isNew;
    if (isNewRow) {
      if (!hasNewLineItemData(item, key)) return;
    } else if (!item?.id || (!hasLineItemChanges(item, key) && !discountChanged)) {
      return;
    }

    const quantity = quantityPreview;
    const retailPrice = retailPreview;
    const actualPrice = actualPreview;
    const discount = discountPreview;

    setLineItemSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const response = isNewRow
        ? await axiosInstance.post('/api/v1/doc/line-item', {
            doc_id: detailedData.id,
            part_number: getLineItemValue(item, key, 'part_number'),
            description: getLineItemValue(item, key, 'description'),
            quantity,
            retail_price: retailPrice,
            actual_price: actualPrice,
          })
        : await axiosInstance.put('/api/v1/doc/line-item', {
            id: item.id,
            doc_id: detailedData.id,
            part_number: getLineItemValue(item, key, 'part_number'),
            description: getLineItemValue(item, key, 'description'),
            quantity,
            retail_price: retailPrice,
            actual_price: actualPrice,
            discount,
          });
      const updatedDiscount = response?.data?.data?.overall_discount;
      const updatedLineItem = response?.data?.data?.line_item;
      if (updatedDiscount !== undefined) {
        setLineItemDocDiscount(updatedDiscount);
      }
      if (updatedLineItem) {
        setLineItemsState((prev) =>
          prev.map((existing) => {
            if (String(existing.id) === String(updatedLineItem.id)) {
              return { ...existing, ...updatedLineItem, __isNew: false };
            }
            if (existing.__isNew && String(existing.id) === String(item.id)) {
              return { ...updatedLineItem, __isNew: false };
            }
            return existing;
          })
        );
      }
      setLineItemStatus((prev) => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => {
        setLineItemStatus((prev) => {
          if (prev[key] !== 'saved') return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 1500);
      setLineItemEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (isNewRow) {
        setAddingLineItem(false);
      }
      await refetchData?.();
    } catch (error) {
      console.error('Failed to update line item', error);
      setLineItemStatus((prev) => ({ ...prev, [key]: 'error' }));
      setTimeout(() => {
        setLineItemStatus((prev) => {
          if (prev[key] !== 'error') return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 2500);
    } finally {
      setLineItemSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleLineItemDelete = async () => {
    if (!deleteTarget || !isAdmin) return;
    const { item, key } = deleteTarget;
    closeDeleteConfirm();
    setLineItemDeleting((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await axiosInstance.delete(`/api/v1/doc/line-item/${item.id}`);
      const updatedDiscount = response?.data?.data?.overall_discount;
      if (updatedDiscount !== undefined) {
        setLineItemDocDiscount(updatedDiscount);
      }
      setLineItemsState((prev) => prev.filter((existing) => String(existing.id) !== String(item.id)));
      await refetchData?.();
    } catch (error) {
      console.error('Failed to delete line item', error);
    } finally {
      setLineItemDeleting((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openDeleteConfirm = (event, item, index) => {
    if (!item?.id || !isAdmin) return;
    const key = getLineItemKey(item, index);
    setDeleteAnchorEl(event.currentTarget);
    setDeleteTarget({ item, key });
  };

  const closeDeleteConfirm = () => {
    setDeleteAnchorEl(null);
    setDeleteTarget(null);
  };

  const handleAddLineItem = () => {
    if (addingLineItem) return;
    const tempId = `new-${Date.now()}`;
    setLineItemsState((prev) => ([
      {
        id: tempId,
        __isNew: true,
        part_number: '',
        description: '',
        quantity: '',
        retail_price: '',
        actual_price: '',
        discount: null,
      },
      ...prev,
    ]));
    setAddingLineItem(true);
  };

  const handlePreviousImageManual = useCallback(() => {
    shouldAutoNavigateRef.current = false;
    suppressAutoFocusRef.current = true;
    handlePreviousImageProp();
  }, [handlePreviousImageProp]);

  const handleNextImageManual = useCallback(() => {
    shouldAutoNavigateRef.current = false;
    suppressAutoFocusRef.current = true;
    handleNextImageProp();
  }, [handleNextImageProp]);

  const handleZoomIn = useCallback(() => {
    if (transformWrapperRef?.current?.zoomIn) {
      transformWrapperRef.current.zoomIn(0.4);
    }
  }, [transformWrapperRef]);

  const handleZoomOut = useCallback(() => {
    if (transformWrapperRef?.current?.zoomOut) {
      transformWrapperRef.current.zoomOut(0.4);
    }
  }, [transformWrapperRef]);

  const supplierMap = suppliers.reduce((acc, supplier) => {
    acc[supplier.supplier_id] = supplier.name;
    return acc;

  }, {});

  const siteMap = sites.reduce((acc, site) => {
    acc[site.location_id] = site.name;
    return acc;
  }, {});

  const validateAllFields = useCallback(() => {
    const errors = {};
    const normalizedDocTypeId = normalizeId(tempEditableData?.doc_type_id);
    const isStrictDocType = normalizedDocTypeId && STRICT_DOC_TYPES.has(normalizedDocTypeId);
    const isStatement = normalizedDocTypeId === STATEMENT_DOC_TYPE;

    const requiredFields = ['doc_type_id'];
    if (isStrictDocType) {
      requiredFields.push(...STRICT_REQUIRED_FIELDS);
    } else if (isStatement) {
      requiredFields.push('location_id', 'doc_status_id', 'invoice_date', 'total');
    }

    requiredFields.forEach((field) => {
      const value = tempEditableData?.[field];
      if (isEmptyValue(value)) {
        errors[field] = 'This field is required';
      }
    });

    DATE_FIELDS.forEach((field) => {
      if (errors[field]) return;
      const value = tempEditableData?.[field];
      if (isEmptyValue(value)) return;
      if (!isValidDate(value)) {
        errors[field] = 'Use dd/mm/yyyy date format.';
      }
    });

    setValidationErrors(errors);
  }, [tempEditableData]);

  useEffect(() => {
    validateAllFields();
  }, [tempEditableData, validateAllFields]);

  useEffect(() => {
    setLineItemsState(lineItems);
  }, [lineItems]);

  useEffect(() => {
    setLineItemEdits({});
    setLineItemSaving({});
    setLineItemDeleting({});
    setLineItemDocDiscount(null);
    setLineItemStatus({});
    setLineItemsState(lineItems);
    setAddingLineItem(false);
  }, [detailedData?.id]);

  useEffect(() => {
      setLineItemDocDiscount(null);
    }, [detailedData?.discount]);

  useEffect(() => {
    setAddingLineItem(false);
  }, [lineItemsState]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedDocuments = otherDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleKeyDown = useCallback((event) => {
    if (event.defaultPrevented) return;

    const target = event.target;
    if (target && (target.isContentEditable || target.closest('input, textarea, select, button, [contenteditable="true"], svg, canvas'))) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      handlePreviousImageManual();
    } else if (event.key === 'ArrowRight') {
      handleNextImageManual();
    }
  }, [handlePreviousImageManual, handleNextImageManual]);



  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const lastPolygonFocusRef = useRef('');

  const updateImageSize = useCallback((property) => {
    const rawProperty = typeof property === 'string' ? property : polygonPropertyRef.current;
    if (!rawProperty) return;

    const [baseProperty, stamp = ''] = rawProperty.split('::');
    if (!baseProperty) return;

    const focusKey = stamp || baseProperty;
    if (!imageRef.current || !detailedData?.polygons?.length) return;

    const pageProperty = detailedData.polygons.find((polygon) => polygon?.property === baseProperty);
    if (!pageProperty) return;

    const targetIndex = (pageProperty.page ?? 1) - 1;
    if (targetIndex < 0) return;

    if (currentImageIndex !== targetIndex) {
      if (!shouldAutoNavigateRef.current) {
        suppressAutoFocusRef.current = false;
        return;
      }
      lastPolygonFocusRef.current = focusKey;
      if (suppressAutoFocusRef.current) {
        suppressAutoFocusRef.current = false;
        return;
      }

      if (currentImageIndex < targetIndex) handleNextImageProp();
      else handlePreviousImageProp();
      setTimeout(() => updateImageSize(baseProperty), 0);
      return;
    }

    shouldAutoNavigateRef.current = false;
    suppressAutoFocusRef.current = false;

    const pageImage = detailedData.images?.[targetIndex];
    if (!pageImage?.data) return;

    const img = new Image();
    img.src = pageImage.data;
    img.onload = () => {
      if (!imageRef.current) return;

      setImageSize({
        width: imageRef.current.clientWidth,
        height: imageRef.current.clientHeight,
        offsetX: imageRef.current.offsetLeft,
        offsetY: imageRef.current.offsetTop,
      });

      const padding = 2;
      const scaledPolygons = [];

      detailedData.polygons.forEach((polygon) => {
        const originalWidth = img.width;
        const originalHeight = img.height;
        const scaleX = imageRef.current.clientWidth / originalWidth;
        const scaleY = imageRef.current.clientHeight / originalHeight;

        const points = [];
        polygon?.points?.forEach((point, index) => {
          if (index === 0) {
            points.push({ x: Math.round(point[0] * scaleX) - padding, y: Math.round(point[1] * scaleY) - padding });
          } else if (index === 1) {
            points.push({ x: Math.round(point[0] * scaleX) + padding, y: Math.round(point[1] * scaleY) - padding });
          } else if (index === 2) {
            points.push({ x: Math.round(point[0] * scaleX) + padding, y: Math.round(point[1] * scaleY) + padding });
          } else {
            points.push({ x: Math.round(point[0] * scaleX) - padding, y: Math.round(point[1] * scaleY) + padding });
          }
        });

        scaledPolygons.push({
          page: polygon?.page,
          display: baseProperty === polygon?.property,
          points,
        });
      });

      setPolygons(scaledPolygons);
      lastPolygonFocusRef.current = focusKey;
    };
  }, [currentImageIndex, detailedData, handleNextImageProp, handlePreviousImageProp]);

  const prevPolygonPropertyRef = useRef('');

  useEffect(() => {
    const hasNewPolygonProperty = polygonProperty && polygonProperty !== prevPolygonPropertyRef.current;
    prevPolygonPropertyRef.current = polygonProperty;

    if (hasNewPolygonProperty) {
      shouldAutoNavigateRef.current = true;
    }

    polygonPropertyRef.current = polygonProperty;
    updateImageSize(polygonProperty);
  }, [polygonProperty, updateImageSize]);

  useEffect(() => {
    lastPolygonFocusRef.current = '';
    setPolygonProperty('');
    setPolygons([]);
    shouldAutoNavigateRef.current = false;
  }, [detailedData?.id]);

  // Update image dimensions when the image loads
  useEffect(() => {
    window.addEventListener('resize', updateImageSize);
    return () => {
      window.removeEventListener('resize', updateImageSize);
    };
  }, [updateImageSize]);

  const handleDownload = () => {
    // Convert Base64 string to a Blob object
    const pdfBlob = new Blob([Uint8Array.from(atob(detailedData.files[0].data), c => c.charCodeAt(0))], { type: 'application/pdf' });
    
    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfBlob);
    link.download = `${detailedData.files[0].name.split("/")[1]}`; // The file name that will be downloaded
    document.body.appendChild(link);
    link.click();

    // Clean up by removing the link
    document.body.removeChild(link);
  };

  const downloadFileName = useMemo(() => {
    if (!Array.isArray(detailedData?.files) || detailedData.files.length === 0) {
      return '';
    }
    const rawName = detailedData.files[0]?.name || '';
    if (!rawName) return '';
    const segments = rawName.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : rawName;
  }, [detailedData?.files]);

  const canDownload = Boolean(downloadFileName && detailedData?.files?.[0]?.data);

  const rawLink = useMemo(() => {
    if (!detailedData?.link || typeof detailedData.link !== 'string') {
      return '';
    }
    return detailedData.link.trim();
  }, [detailedData?.link]);

  const folderLink = useMemo(() => {
    if (!rawLink) return '';
    const lastSlashIndex = rawLink.lastIndexOf('/');
    if (lastSlashIndex === -1) return rawLink;
    const protocolIndex = rawLink.indexOf('://');
    const domainSlashThreshold = protocolIndex >= 0 ? protocolIndex + 2 : -1;
    if (lastSlashIndex <= domainSlashThreshold) {
      return rawLink;
    }
    return rawLink.slice(0, lastSlashIndex);
  }, [rawLink]);

  const openLinkInNewTab = useCallback((targetUrl) => {
    if (!targetUrl) return;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenFolderLink = useCallback(() => {
    openLinkInNewTab(folderLink);
  }, [folderLink, openLinkInNewTab]);

  const handleOpenFileLink = useCallback(() => {
    openLinkInNewTab(rawLink);
  }, [rawLink, openLinkInNewTab]);

  return (
    <div style={{ paddingBottom: '40px' }}>
      <Grid container spacing={4} alignItems="stretch">
        <Grid
          item
          xs={12}
          md={8}
          sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
              mb: 1,
              pb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', flex: '0 0 auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBackClick}
                  size="small"
                >
                  Back to Grid
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  flex: '1 1 auto',
                  ml: { xs: 0, sm: -1 },
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowLeft />}
                  onClick={onPreviousDocument}
                  disabled={!hasPreviousDocument || documentLoading}
                  size="small"
                  sx={{ typography: 'body2', mr: { sm: 1.25 } }}
                >
                  Prev Doc
                </Button>
                {documentCount > 0 && documentPosition > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mr: { sm: 1.25 } }}>
                    Document {documentPosition} of {documentCount}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  endIcon={<ArrowRight />}
                  onClick={onNextDocument}
                  disabled={!hasNextDocument || documentLoading}
                  size="small"
                  sx={{ typography: 'body2', ml: { sm: 1.25 } }}
                >
                  Next Doc
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flex: '0 0 auto',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  minWidth: { xs: 'auto', sm: 180 },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Net Total: {formatCurrency(totalNetValue || 0)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr auto 1fr',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifySelf: { xs: 'center', sm: 'start' },
                }}
              >
                {(tempEditableData?.job_number || tempEditableData?.job_id) && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      display: 'flex',
                      gap: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    {/* <Box component="span" sx={{ pointerEvents: 'none' }}>
                      Job Number:
                    </Box> */}
                    <Box component="span" sx={{ userSelect: 'text' }}>
                      {(tempEditableData.job_number || tempEditableData.job_id).toUpperCase() +
                        ((tempEditableData.vehicle_reg ? " / " + tempEditableData.vehicle_reg.toUpperCase() : ""))}
                    </Box>
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  justifySelf: 'center',
                }}
              >
                <IconButton
                  size="small"
                  onClick={handlePreviousImageManual}
                  disabled={
                    currentImageIndex === 0 ||
                    !Array.isArray(detailedData?.images) ||
                    detailedData.images.length === 0
                  }
                >
                  <ArrowLeft />
                </IconButton>
                {Array.isArray(detailedData?.images) && detailedData.images.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Page {currentImageIndex + 1} of {detailedData.images.length}
                  </Typography>
                )}
                <IconButton
                  size="small"
                  onClick={handleNextImageManual}
                  disabled={
                    !Array.isArray(detailedData?.images) ||
                    currentImageIndex === detailedData.images.length - 1
                  }
                >
                  <ArrowRight />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-end' },
                  gap: 1,
                  justifySelf: { xs: 'center', sm: 'end' },
                  flexWrap: 'wrap',
                }}
              >
                {(lineItemDocDiscount !== null && lineItemDocDiscount !== undefined) && formatPercent(lineItemDocDiscount) && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Discount: {formatPercent(lineItemDocDiscount)}
                  </Typography>
                )}
                {(lineItemDocDiscount === null || lineItemDocDiscount === undefined) &&
                  (tempEditableData?.discount !== null && tempEditableData?.discount !== undefined) && formatPercent(tempEditableData.discount) && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Discount: {formatPercent(tempEditableData.discount)}
                  </Typography>
                )}
                {(lineItemDocDiscount === null || lineItemDocDiscount === undefined) &&
                  (tempEditableData?.discount === null || tempEditableData?.discount === undefined) &&
                  (detailedData?.discount !== null && detailedData?.discount !== undefined) && formatPercent(detailedData.discount) && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Discount: {formatPercent(detailedData.discount)}
                    </Typography>
                  )}
                {rawLink && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Open document folder">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="open document folder"
                          onClick={handleOpenFolderLink}
                        >
                          <Folder fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Open source file">
                      <IconButton
                        size="small"
                        aria-label="open source file"
                        onClick={handleOpenFileLink}
                      >
                        <InsertDriveFile fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Tooltip title={downloadFileName || 'Download source file'}>
                  <span>
                    <Button
                      onClick={handleDownload}
                      size="small"
                      startIcon={<FileDownload fontSize="small" />}
                      variant="text"
                      disabled={!canDownload}
                    >
                      Download
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'stretch',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              bgcolor: '#fafafa',
              minHeight: 0,
              overflow: 'hidden',
              maxHeight: '88vh',
            }}
          >
            <TransformWrapper
              ref={transformWrapperRef}
              initialScale={1}
              minScale={0.6}
              limitToBounds={false}
              style={{ width: '100%', height: '100%' }}
            >
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }} contentStyle={{ width: '100%', height: '100%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    overflow: 'auto',
                  }}
                >
                  <img
                    ref={imageRef}
                    src={detailedData.images[currentImageIndex].data}
                    alt="Document"
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      cursor: 'zoom-in',
                    }}
                  />

                  <svg
                    width={imageSize.width}
                    height={imageSize.height}
                    style={{
                      position: 'absolute',
                      top: imageSize.offsetY ?? 0,
                      left: imageSize.offsetX ?? 0,
                      pointerEvents: 'none',
                    }}
                  >
                    {polygons.map((polygon, index) =>
                      polygon?.display && polygon?.page === currentImageIndex + 1 ? (
                        <polygon
                          key={index}
                          points={polygon?.points.map((point) => `${point.x},${point.y}`).join(' ')}
                          stroke="red"
                          strokeWidth="2"
                          fill="none"
                        />
                      ) : null
                    )}
                  </svg>
                </Box>
              </TransformComponent>
            </TransformWrapper>
            <Box
              sx={{
                position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 1,
                backgroundColor: 'rgba(220, 220, 220, 0.3)', borderRadius: '8px', p: 0.5
              }}
            >
              <IconButton size="small" onClick={handleZoomIn}>
                <ZoomIn fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleZoomOut}>
                <ZoomOut fontSize="small" />
              </IconButton>
            </Box>
          </Box>

        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
          <Box sx={{ flexGrow: 1, display: 'flex' }}>
          <ExtractedFields
            tempEditableData={tempEditableData}
            sites={sites}
            suppliers={suppliers}
            nominalCodes={nominalCodes}
            taxCodes={taxCodes}
            docTypes={docTypes}
            docStatuses={docStatuses}
            originalData={editableData}
            handleFieldChange={handleFieldChange}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            dateInput={tempEditableData.invoice_date}
            setDateInput={(value) => handleFieldChange('invoice_date', value)}
            isEdited={isEdited}
            handleSaveChanges={handleSaveChanges}
            onApproveDocument={handleApproveForImport}
            onRejectDocument={handleRejectDocument}
            docRejectReasons={docRejectReasons}
            getLocationOptionLabel={getLocationOptionLabel}
            getSupplierOptionLabel={getSupplierOptionLabel}
            getNominalCodeOptionLabel={getNominalCodeOptionLabel}
            getDocTypeOptionLabel={getDocTypeOptionLabel}
            getDocStatusOptionLabel={getDocStatusOptionLabel}
            refetchData={refetchData}
            imageName={imageName} // pass image name here
            setPolygonProperty={setPolygonProperty}
            validateAllFields={validateAllFields}
            snackbarAnchor={{ vertical: 'top', horizontal: 'right' }}
            snackbarSx={{ top: 16, right: 16, left: 'auto', bottom: 'auto' }}
            isReadOnly={isReadOnly}
          />
          </Box>
        </Grid>
      </Grid>

      {showOtherDocuments && (
        <Box sx={{ mt: 4 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Uploaded On</strong></TableCell>
                  <TableCell><strong>Invoice Date</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell><strong>Document ID</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedDocuments.map((data, index) => {
                  const isSelected = detailedData && data.id === detailedData.id;
                  return (
                    <TableRow
                      key={index}
                      onClick={() => handleCardClick(data.id, { globalIndex: data.__sequenceIndex })}
                      hover
                      selected={isSelected}
                      style={{ cursor: 'pointer' }}
                      sx={isSelected ? { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : undefined}
                    >
                      <TableCell>{data.uploaded_on}</TableCell>
                      <TableCell>{data.invoice_date}</TableCell>
                      <TableCell>{siteMap[data.location_id] || 'N/A'}</TableCell>
                      <TableCell>{supplierMap[data.supplier_id] || 'N/A'}</TableCell>
                      <TableCell>{(parseFloat(data.total)).toFixed(2)}</TableCell>
                      <TableCell>{data.invoice_number}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {otherDocuments.length > itemsPerPage && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={Math.ceil(otherDocuments.length / itemsPerPage)}
                page={currentPage}
                onChange={handleChangePage}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mx: 'auto' }}>
            Line Items
          </Typography>
          <Button variant="outlined" size="small" onClick={handleAddLineItem} disabled={addingLineItem || isReadOnly}>
            Add Line Item
          </Button>
        </Box>
        {lineItemsState.length > 0 ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table
              size="medium"
              sx={{
                tableLayout: 'fixed',
                '& .MuiTableCell-root': { py: 1.5 },
                '& thead th': {
                  backgroundColor: '#f4f9ff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: 13,
                  borderBottomColor: '#e0e0e0',
                },
                '& tbody tr': {
                  borderBottom: '1px solid #f0f0f0',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Retail Price</TableCell>
                  <TableCell align="right">Actual Price</TableCell>
                  <TableCell align="right">Discount (%)</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItemsState.map((item, index) => {
                  const rowKey = getLineItemKey(item, index);
                  const displayLineIndex = Number.isInteger(item?.line_index) ? item.line_index : index + 1;
                  const quantityValue = getLineItemValue(item, rowKey, 'quantity');
                  const retailValue = getLineItemValue(item, rowKey, 'retail_price');
                  const actualValue = getLineItemValue(item, rowKey, 'actual_price');
                  const hasEdits = !!lineItemEdits[rowKey];
                  const isNewRow = !!item.__isNew;
                  const computedDiscount = hasEdits
                    ? calculateLineDiscount(
                        parseNumber(quantityValue),
                        parseNumber(retailValue),
                        parseNumber(actualValue)
                      )
                    : null;
                  const discountDisplay = computedDiscount !== null ? computedDiscount : item.discount;
                  const discountChanged = computedDiscount !== null && Number(computedDiscount) !== Number(item.discount);
                  const canSave = !lineItemSaving[rowKey] && (
                    (isNewRow && hasNewLineItemData(item, rowKey)) ||
                    (!isNewRow && item?.id && (hasLineItemChanges(item, rowKey) || (hasEdits && discountChanged)))
                  );

                  return (
                  <TableRow key={rowKey}>
                    <TableCell>{displayLineIndex}</TableCell>
                    <TableCell>
                      <TextField
                        value={getLineItemValue(item, rowKey, 'part_number')}
                        onChange={(event) => handleLineItemChange(rowKey, 'part_number', event.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: 160 }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <TextField
                        value={getLineItemValue(item, rowKey, 'description')}
                        onChange={(event) => handleLineItemChange(rowKey, 'description', event.target.value)}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: 280 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={quantityValue}
                        onChange={(event) => handleLineItemChange(rowKey, 'quantity', event.target.value)}
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: 'decimal' }}
                        sx={{ maxWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={retailValue}
                        onChange={(event) => handleLineItemChange(rowKey, 'retail_price', event.target.value)}
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: 'decimal' }}
                        sx={{ maxWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={actualValue}
                        onChange={(event) => handleLineItemChange(rowKey, 'actual_price', event.target.value)}
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: 'decimal' }}
                        sx={{ maxWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatPercentOrDash(discountDisplay)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleLineItemSave(item, index)}
                          disabled={!canSave}
                          sx={{ minWidth: 72 }}
                        >
                          {lineItemSaving[rowKey] ? 'Saving...' : 'Save'}
                        </Button>
                        {lineItemStatus[rowKey] === 'saved' && (
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                            Saved
                          </Typography>
                        )}
                        {lineItemStatus[rowKey] === 'error' && (
                          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                            Failed
                          </Typography>
                        )}
                        {isAdmin && (
                          <IconButton
                            size="small"
                            onClick={(event) => openDeleteConfirm(event, item, index)}
                            disabled={lineItemDeleting[rowKey]}
                            color="error"
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )}
                        {isNewRow && !lineItemSaving[rowKey] && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setLineItemsState((prev) => prev.filter((existing) => String(existing.id) !== String(item.id)))}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No line items available for this document.
          </Typography>
        )}
      </Box>

      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={closeDeleteConfirm}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 1.5, borderRadius: 1.5 } }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Delete this line item?
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={closeDeleteConfirm}>
            Cancel
          </Button>
          <Button size="small" color="error" variant="contained" onClick={handleLineItemDelete}>
            Delete
          </Button>
        </Box>
      </Popover>

    </div>
  );
};

export default ReviewPage;
